import Add from '@mui/icons-material/Add';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  AddOnRateType,
  deleteV1CompaniesCompanyIdAutoAddOnAssignmentsId,
  RateOwnerType,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useContext, useEffect, useRef, useState } from 'react';

import DataGridPager from '~components/DataGrid/DataGridPager';
import actionsColumn from '~components/DataTable/actionsColumn';
import DataTable from '~components/DataTable/DataTable';
import { DataTableColumn } from '~components/DataTable/dataTableColumn';
import { SimpleMenu } from '~components/Menu/SimpleMenu';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { AddOnAssignment } from '~hooks/useAddOns/models';
import { rateTypeTextByAddOnRateType } from '~pages/Settings/RatesManagement/AddOnCharges/addOnChargesDataGridColumnsDefinition';
import reportRequestError from '~services/reportRequestError';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';
import { formatPercentage } from '~utils/utilFunctions';

import { OwnerContext } from '../context/OwnerContext';
import AutomaticAddOnEditDialog from './AutomaticAddOnEditDialog';
import AutomaticAddonsQueryContext, {
  AutomaticAddonsQueryContextProvider,
} from './AutomaticAddonsQueryContext';
import AutomaticSettlementAddOnsDialog from './AutomaticSettlementAddOnsDialog';
interface AutomaticAddonsListContainerProps {
  ownerId: string;
  ownerType: RateOwnerType;
}

export default observer(function AutomaticAddonsListContainer({
  ownerId,
  ownerType,
}: AutomaticAddonsListContainerProps) {
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);

  return (
    <AutomaticAddonsQueryContextProvider
      companyId={companyId}
      ownerId={ownerId}
      ownerType={ownerType}
    >
      <AutomaticAddonsList companyId={companyId} />
    </AutomaticAddonsQueryContextProvider>
  );
});

const AutomaticAddonsList = ({ companyId }: { companyId: string }) => {
  const { data, goToPage, isFetching, pagination, reloadCurrentPage } = useContext(
    AutomaticAddonsQueryContext,
  );
  const { selectedOwnerId, selectedOwnerName, selectedOwnerType } =
    useContext(OwnerContext);
  const [openDialog, setOpenDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const isGridBlocked = isFetching || isDeleting;
  const { toasterStore } = useStores();
  const [selectedAddOnAssignment, setSelectedAddOnAssignment] =
    useState<AddOnAssignment | null>(null);

  const selectedOwnerIdRef = useRef(selectedOwnerId);
  const selectedOwnerTypeRef = useRef(selectedOwnerType);

  useEffect(() => {
    selectedOwnerIdRef.current = selectedOwnerId;
    selectedOwnerTypeRef.current = selectedOwnerType;
  }, [selectedOwnerId, selectedOwnerType]);

  const handleDeleteAutoAddOn = async (row: AddOnAssignment) => {
    const currentOwnerId = selectedOwnerIdRef.current;
    const currentOwnerType = selectedOwnerTypeRef.current;

    if (!currentOwnerId || !currentOwnerType || !row.id) {
      return;
    }

    setIsDeleting(true);
    try {
      await deleteV1CompaniesCompanyIdAutoAddOnAssignmentsId({
        path: { 'company-id': companyId, id: row.id },
      });
      toasterStore.push(
        alert(
          t('administration.automatic_settlement_add_ons.add_on_deleted'),
          AlertTypes.success,
        ),
      );
      await reloadCurrentPage();
    } catch (error) {
      reportRequestError(
        error,
        t('error_messages.auto_add_on_assignments.failed_to_delete'),
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const onDelete = (row: AddOnAssignment) => {
    handleDeleteAutoAddOn(row);
  };

  const handleAddAddOns = () => {
    setOpenDialog(true);
  };

  const editRow = (row: AddOnAssignment) => {
    setSelectedAddOnAssignment(row);
    setOpenEditDialog(true);
  };

  const columns: Array<DataTableColumn<AddOnAssignment>> = [
    {
      field: 'add_on',
      headerName: t('administration.automatic_settlement_add_ons.add_on'),
      valueGetter: (row) => row.name,
    },
    {
      field: 'rate_type',
      headerName: t('administration.automatic_settlement_add_ons.rate_type'),
      valueGetter: (row) =>
        row.revenueCostRateType
          ? rateTypeTextByAddOnRateType[row.revenueCostRateType]
          : '',
    },
    {
      field: 'default_qty',
      headerName: t('administration.rates.automatic_add_ons.quantity'),
      valueGetter: (row) => row.quantity,
    },
    {
      field: 'cost_rate',
      headerName: t('administration.rates.add_ons.cost_rate'),
      valueGetter: (row) => row.costRateValue,
      renderCell: (row) => {
        if (row.revenueCostRateType === AddOnRateType.RATE_PERCENT_OF_TOTAL) {
          return formatPercentage(row.costRateValue);
        }
        return `$${row.costRateValue ?? 0}`;
      },
    },
    {
      field: 'invoice_rate',
      headerName: t('administration.rates.invoice_rate'),
      valueGetter: (row) =>
        row.revenueCostRateType === AddOnRateType.RATE_PERCENT_OF_TOTAL
          ? row.percentage
          : row.rate,
      renderCell: (row) => {
        if (row.revenueCostRateType === AddOnRateType.RATE_PERCENT_OF_TOTAL) {
          return formatPercentage(row.percentage);
        }
        return `$${row.rate}`;
      },
    },
    {
      field: 'total_installments',
      headerName: t('administration.automatic_settlement_add_ons.total_installments'),
      renderCell: (row) => {
        const applied = row.autoAddOnInstallmentsCount;
        const total = row.totalInstallments;
        const isCapped = total > 0;

        const label = isCapped
          ? t('administration.automatic_settlement_add_ons.installments_progress', {
              applied,
              total,
            })
          : t('administration.automatic_settlement_add_ons.installments_ongoing');

        return (
          <Tooltip
            title={
              isCapped
                ? t('administration.automatic_settlement_add_ons.installments_tooltip', {
                    applied,
                    total,
                  })
                : t(
                    'administration.automatic_settlement_add_ons.installments_tooltip_ongoing',
                  )
            }
          >
            <span>{label}</span>
          </Tooltip>
        );
      },
    },
    actionsColumn((row) => {
      const options = [];

      if (!row.sourceId) {
        options.push({
          callBack: () => onDelete(row),
          icon: <Delete />,
          title: `${t('actions.delete')}`,
        });
      }

      return (
        <>
          <IconButton size="small" onClick={() => editRow(row)}>
            <Edit />
          </IconButton>

          {options.length > 0 && (
            <SimpleMenu options={options} size="small">
              <MoreHoriz />
            </SimpleMenu>
          )}
        </>
      );
    }),
  ];

  return (
    <>
      <Box
        sx={{
          m: 1.5,
          alignItems: 'center',
          display: 'flex',
          gap: 2,
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {selectedOwnerName && (
            <Typography variant="form-section-title" noWrap>
              {selectedOwnerName}
            </Typography>
          )}
        </Box>
        <DataGridPager
          isFetching={isGridBlocked}
          onNavigate={(link) => void goToPage(link)}
          pagination={pagination}
        />
        <SmallButton
          color="brandV2Yellow"
          startIcon={<Add />}
          onClick={handleAddAddOns}
          sx={{ my: '2px' }}
        >
          {t('administration.automatic_settlement_add_ons.add_on')}
        </SmallButton>
      </Box>
      <Divider />
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <DataTable
          id="automatic-settlement-add-ons-grid"
          columns={columns}
          hideToolbar
          loading={isGridBlocked}
          rows={data}
        />
      </Box>
      <AutomaticSettlementAddOnsDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        onSuccess={() => {
          void goToPage();
          setOpenDialog(false);
          toasterStore.push(
            alert(
              t('administration.automatic_settlement_add_ons.add_on_added'),
              AlertTypes.success,
            ),
          );
        }}
        selectedOwnerId={selectedOwnerId}
        selectedOwnerName={selectedOwnerName}
        selectedOwnerType={selectedOwnerType}
      />
      <AutomaticAddOnEditDialog
        companyId={companyId}
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        onSuccess={() => {
          void reloadCurrentPage();
          setOpenEditDialog(false);
        }}
        selectedAutoAddOnAssignment={selectedAddOnAssignment}
      />
    </>
  );
};
